import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchUploads,
  fetchUploadsWithDetails,
  fetchFile,
  checkFilePassword,
  recordPasswordAccess,
  deleteFile,
  sendFileEmail,
} from "../../store/thunks/uploadsThunk";
import FileExplorer from "../../applicationUi/components/FileExplorer";

export default function ClientUploads({ client, clientId }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const actualClientId = client?._id || clientId;
  const [revealedPasswordFiles, setRevealedPasswordFiles] = useState({});
  const [uploadsWithDetails, setUploadsWithDetails] = useState([]);
  const [existingFolders, setExistingFolders] = useState([]);

  useEffect(() => {
    if (actualClientId) {
      dispatch(fetchUploads(actualClientId))
        .unwrap()
        .then((result) => {
          console.log("fetchUploads IN UPLOADS FROM THUNK:", result);
        })
        .catch((error) => {
          console.error("Failed to fetch uploads:", error);
        });
    }
  }, [dispatch, actualClientId]);

  useEffect(() => {
    if (actualClientId) {
      dispatch(fetchUploadsWithDetails(actualClientId))
        .unwrap()
        .then((result) => {
          if (result?.files && Array.isArray(result.files)) {
            setUploadsWithDetails(result.files);
            // Extract folder names from file paths
            const folderSet = new Set();
            result.files.forEach((file) => {
              const folderName = file.path.split("/")[0];
              folderSet.add(folderName);
            });
            setExistingFolders(Array.from(folderSet));
          }
        })
        .catch((error) => {
          console.error("Failed to fetch uploads with details:", error);
        });
    }
  }, [dispatch, actualClientId]);

  const handleFileClick = (fileName) => {
    const sanitizedFileName = fileName.replace(/^\//, "");
    dispatch(
      fetchFile({ clientId: actualClientId, fileName: sanitizedFileName })
    )
      .unwrap()
      .then((result) => {
        const url = URL.createObjectURL(
          new Blob([result.fileBlob], { type: result.fileType })
        );
        const newTab = window.open(url, "_blank");
        if (newTab) {
          newTab.document.write(
            `<iframe src="${url}" style="width:100%; height:100%;" frameborder="0"></iframe>`
          );
          URL.revokeObjectURL(url);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch file:", err);
      });
  };

  const getFileContent = async (fileName) => {
    const sanitizedFileName = fileName.replace(/^\//, "");
    try {
      const result = await dispatch(
        fetchFile({ clientId: actualClientId, fileName: sanitizedFileName })
      ).unwrap();

      // Convert blob to base64
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          // Remove the data:*/*;base64, prefix
          const base64Content = reader.result.split(",")[1];
          resolve(base64Content);
        };
        reader.onerror = reject;
        reader.readAsDataURL(new Blob([result.fileBlob]));
      });
    } catch (err) {
      console.error("Failed to get file content:", err);
      throw err;
    }
  };

  const handleDeleteFile = (fileName) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete this file? ${fileName}\nThis action cannot be undone.`
    );
    if (!confirmDelete) return;

    dispatch(deleteFile({ clientId: actualClientId, fileName }))
      .unwrap()
      .then(() => {
        setUploadsWithDetails((prevFiles) =>
          prevFiles.filter((file) => file.path !== fileName)
        );
        alert("File deleted successfully!");
      })
      .catch((err) => {
        console.error("Error deleting file:", err);
        alert("Failed to delete file.");
      });
  };

  const handleSendEmail = async (file) => {
    console.log("handleSendEmail called with file:", file);
    try {
      const fileContent = await getFileContent(file.path);
      console.log("Got file content, dispatching sendFileEmail");

      const resultAction = await dispatch(
        sendFileEmail({
          clientId: actualClientId,
          fileName: file.path.split("/").pop(),
          recipientEmail: client.email,
          fileContent,
        })
      ).unwrap();

      console.log("Email sent successfully:", resultAction);
      return { success: true };
    } catch (error) {
      console.error("Failed to send email:", error);
      return { success: false, error };
    }
  };

  const renderFiles = (files) => {
    if (!files || files.length === 0) {
      return <p className="text-sm text-gray-500">No files found.</p>;
    }
    return (
      <FileExplorer
        files={uploadsWithDetails}
        clientId={actualClientId}
        client={client}
        onFileClick={handleFileClick}
        onDeleteFile={handleDeleteFile}
        getFileContent={getFileContent}
        onSendEmail={handleSendEmail}
      />
    );
  };

  const handleRevealPassword = (fileName) => {
    const file = uploadsWithDetails.find((file) => file.path === fileName);
    if (file && file.isPasswordProtected) {
      setRevealedPasswordFiles((prevState) => ({
        ...prevState,
        [fileName]: file.password,
      }));
    } else {
      console.error(
        "Failed to retrieve password or file is not password protected"
      );
    }
  };

  const handlePasswordRequired = (fileName) => {
    const password = prompt("Enter password to view file:");
    if (password) {
      dispatch(
        checkFilePassword({ clientId: actualClientId, fileName, password })
      )
        .unwrap()
        .then((result) => {
          if (result.success) {
            handleFileClick(fileName);
            recordPasswordView(fileName);
          } else {
            alert("Incorrect password");
          }
        })
        .catch((err) => {
          console.error("Failed to check password:", err);
        });
    }
  };

  const recordPasswordView = (fileName) => {
    const userId = user._id;
    dispatch(
      recordPasswordAccess({ clientId: actualClientId, fileName, userId })
    );
  };

  return (
    <div className="bg-white rounded-sm shadow-sm border border-gray-200 p-4 space-y-3">
      <h3 className="text-base font-medium text-gray-900">Uploaded Documents</h3>
      {uploadsWithDetails.length === 0 ? (
        <div className="text-center py-8">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-sm bg-gray-100">
            <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No documents</h3>
          <p className="mt-1 text-sm text-gray-500">No documents have been uploaded for this client.</p>
        </div>
      ) : (
        renderFiles(uploadsWithDetails)
      )}
    </div>
  );
}
